// frontend/src/pages/dashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/auth';
import { getAgentEmails, getAgentSettings, getLeads } from '../api';
import { EMAIL_PREFERENCES, US_STATES } from '../constants';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { RefreshCw, Mail, Users, Settings, Plus, Search } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const DashboardPage = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [agentEmails, setAgentEmails] = useState([]);
  const [emailPreferences, setEmailPreferences] = useState({});
  const [selectedStates, setSelectedStates] = useState([]);
  const [activeTab, setActiveTab] = useState('leads');

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getLeads();
      if (result.success) {
        // Ensure leads is always an array
        setLeads(Array.isArray(result.data) ? result.data : []);
      } else {
        setErrorMessage(result.message || 'Failed to fetch leads');
        setLeads([]); // Set empty array on error
      }
    } catch (err) {
      setErrorMessage('Failed to load leads. Please try again.');
      console.error('getLeads error:', err);
      setLeads([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch agent emails
  const fetchAgentEmails = useCallback(async () => {
    try {
      const result = await getAgentEmails();
      if (result.success) {
        setAgentEmails(result.data);
      } else {
        setErrorMessage(result.message);
      }
    } catch (err) {
      setErrorMessage('Failed to load email notifications.');
    }
  }, []);

  // Fetch agent settings
  const fetchAgentSettings = useCallback(async () => {
    try {
      const result = await getAgentSettings();
      if (result.success) {
        setEmailPreferences(result.data.emailPreference || EMAIL_PREFERENCES.IMMEDIATE);
        setSelectedStates(result.data.selectedStates || []);
      }
    } catch (err) {
      setErrorMessage('Failed to load settings.');
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    fetchLeads();
    fetchAgentEmails();
    fetchAgentSettings();
  }, [fetchLeads, fetchAgentEmails, fetchAgentSettings]);

  // Filter leads based on search term
  const filteredLeads = Array.isArray(leads) ? leads.filter(lead => {
    if (!searchTerm) return true;
    const query = searchTerm.toLowerCase();
    return (
      (lead.name && lead.name.toLowerCase().includes(query)) ||
      (lead.email && lead.email.toLowerCase().includes(query)) ||
      (lead.phone && lead.phone.toLowerCase().includes(query)) ||
      (lead.state && lead.state.toLowerCase().includes(query)) ||
      (lead.city && lead.city.toLowerCase().includes(query))
    );
  }) : [];

  // Format state display
  const formatState = (stateCode) => {
    if (!stateCode) return 'N/A';
    const stateName = US_STATES[stateCode];
    return stateName ? `${stateName} (${stateCode})` : stateCode;
  };

  // Calculate age from year born if available
  const calculateAge = (yearBorn, currentAge) => {
    if (yearBorn) {
      const currentYear = new Date().getFullYear();
      return currentYear - parseInt(yearBorn);
    }
    return currentAge || 'N/A';
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <div className="flex items-center space-x-4">
          <Button onClick={fetchLeads} variant="outline">
            Refresh
          </Button>
          <Button onClick={() => navigate('/admin/settings')} variant="outline">
            Settings
          </Button>
        </div>
      </div>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {successMessage && (
        <Alert>
          <AlertTitle>Success</AlertTitle>
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="email">Email Settings</TabsTrigger>
          <TabsTrigger value="stats">Statistics</TabsTrigger>
        </TabsList>

        <TabsContent value="leads">
          <Card>
            <CardHeader>
              <CardTitle>Your Leads</CardTitle>
              <CardDescription>
                Manage and track your leads
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center mb-4">
                <div className="relative w-64">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search leads..."
                    className="pl-10 pr-4 py-2 w-full border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="text-sm text-gray-500">
                  {filteredLeads.length} leads found
                </div>
              </div>

              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Age</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <div className="flex justify-center">
                            <div className="h-6 w-6 animate-spin text-primary">Loading...</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : filteredLeads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <div className="text-gray-500">
                            {searchTerm ? 'No leads match your search' : 'No leads available'}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLeads.map((lead) => (
                        <TableRow key={lead.id}>
                          <TableCell className="font-medium">{lead.name}</TableCell>
                          <TableCell>{lead.email}</TableCell>
                          <TableCell>{lead.phone || 'N/A'}</TableCell>
                          <TableCell>{formatState(lead.state)}</TableCell>
                          <TableCell>{calculateAge(lead.year_born, lead.age)}</TableCell>
                          <TableCell>
                            <Badge variant={lead.status === 'purchased' ? 'success' : 'default'}>
                              {lead.status || 'new'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/admin/emails/${lead.id}`)}
                              >
                                Manage Emails
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/admin/leads/${lead.id}`)}
                              >
                                Edit
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle>Email Settings</CardTitle>
              <CardDescription>
                Configure your email preferences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Email Preference</label>
                  <select
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    value={emailPreferences.frequency || 'immediate'}
                    onChange={(e) => setEmailPreferences(prev => ({ ...prev, frequency: e.target.value }))}
                  >
                    <option value="immediate">Immediate</option>
                    <option value="daily">Daily Digest</option>
                    <option value="weekly">Weekly Summary</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium">Selected States</label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {Object.entries(US_STATES).map(([code, name]) => (
                      <label key={code} className="inline-flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-indigo-600"
                          checked={selectedStates.includes(code)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedStates([...selectedStates, code]);
                            } else {
                              setSelectedStates(selectedStates.filter(state => state !== code));
                            }
                          }}
                        />
                        <span className="ml-2 text-sm text-gray-700">{name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stats">
          <Card>
            <CardHeader>
              <CardTitle>Statistics</CardTitle>
              <CardDescription>
                Overview of your leads
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-white rounded-lg shadow">
                  <h3 className="text-lg font-medium">Total Leads</h3>
                  <p className="text-3xl font-bold">{leads.length}</p>
                </div>
                <div className="p-4 bg-white rounded-lg shadow">
                  <h3 className="text-lg font-medium">Available Leads</h3>
                  <p className="text-3xl font-bold">
                    {leads.filter(lead => lead.status !== 'purchased').length}
                  </p>
                </div>
                <div className="p-4 bg-white rounded-lg shadow">
                  <h3 className="text-lg font-medium">Purchased Leads</h3>
                  <p className="text-3xl font-bold">
                    {leads.filter(lead => lead.status === 'purchased').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DashboardPage;